import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalContentProps,
  ModalOverlay,
  ModalProps,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@chakra-ui/react'
import { FormEvent, ReactNode } from 'react'

import { ActionKind } from '../ActionButton/actions'
import FormActions from '../ActionButton/FormActions'

export interface IFormModalTab {
  /** Rótulo da aba. Aceita nó para o caso do contador de erros ao lado do texto. */
  label: ReactNode
  content: ReactNode
}

/**
 * Largura pelo formato do conteúdo, não por gosto.
 *
 * O painel tinha 10 valores de `size` sem relação nenhuma com o que havia dentro:
 * `SectorCreateModal`, com um campo de texto, era `6xl`; `UserCreateModal`, com nove campos,
 * era `lg`. O vocabulário aqui é curto de propósito — se um modal não cabe em nenhum dos
 * quatro, o problema costuma ser o modal.
 */
export type FormModalSize = 'compact' | 'default' | 'wide' | 'editor'

const SIZES: Record<FormModalSize, ModalProps['size']> = {
  /** Um ou dois campos: criar um setor, renomear algo. */
  compact: 'md',
  /** Uma coluna de campos. O caso comum. */
  default: 'xl',
  /** Duas colunas, ou uma tabela dentro do modal. */
  wide: '4xl',
  /** Editor de texto rico, abas, recorte de imagem. */
  editor: '6xl'
}

export interface IFormModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Título em pt-BR. Sai como `h2` — nunca monte um `Heading` dentro do corpo.
   *
   * Omitir é para o modal que não tem título de verdade: uma pré-visualização de imagem, uma
   * busca. Aí não há `ModalHeader`, e o X flutua sobre o conteúdo, que é o comportamento do
   * Chakra.
   */
  title?: ReactNode
  /**
   * Chamado pelo submit do `<form>`: tanto pelo botão de confirmação quanto pelo Enter.
   *
   * O `preventDefault()` já foi dado quando isto roda. O evento vai junto mesmo assim, para os
   * handlers que já o recebiam quando o `<form>` era montado na página.
   *
   * **Omitir torna o modal de leitura**: não há `<form>` nem botão de confirmação, e o rodapé fica
   * só com o Fechar. É como os painéis de detalhe e as pré-visualizações entram aqui.
   */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
  size?: FormModalSize
  /** Cor e rótulo do botão de confirmação, vindos do registry `ACTIONS`. */
  confirmAction?: ActionKind
  confirmLabel?: string
  /** Para quando "Cancelar" ficaria ambíguo — cancelar uma inscrição, por exemplo. */
  cancelLabel?: string
  isLoading?: boolean
  isConfirmDisabled?: boolean
  /** Rodapé próprio, para o modal que tem mais de duas saídas. */
  footer?: ReactNode
  initialFocusRef?: ModalProps['initialFocusRef']
  /**
   * Um modal não dispensável não fecha por Esc, por clique fora **nem pelo X**, que some.
   *
   * Uma prop só para os três porque são a mesma ideia: o painel tinha modais que barravam o
   * Esc e o clique fora e deixavam o X no lugar, o que anula os outros dois. Use quando sair
   * sem decidir deixaria a tela sem dado — o seletor de ano do cronograma, por exemplo.
   */
  isDismissible?: boolean
  /**
   * Geometria do conteúdo, para o modal que os quatro tokens de `size` não alcançam: um editor
   * que precisa de `95vw`, uma tabela larga. **Só medida** — `maxW`, `w`, `h`, `minH`, `maxH`.
   *
   * Cor, raio e sombra não entram aqui: eram 16 valores diferentes espalhados pelas páginas, e
   * unificá-los foi metade do ponto deste componente.
   */
  contentProps?: Pick<ModalContentProps, 'maxW' | 'w' | 'h' | 'minH' | 'maxH'>
  /**
   * Corpo em coluna de altura cheia: o filho marcado com `flex="1"` cresce e rola, e o que está
   * nas pontas fica parado. Para o modal que é uma listagem — filtro em cima, tabela no meio,
   * contagem e paginação embaixo —, onde deixar tudo rolar junto some com o filtro na primeira
   * rolagem.
   *
   * É uma prop, e não um `<Flex h="100%">` dentro do `children`, porque a porcentagem não resolve:
   * o `ModalBody` tira a altura do flex do `ModalContent` e o navegador trata essa altura como
   * indefinida para o filho, que volta a medir pelo conteúdo. O `display: flex` tem de sair do
   * próprio `ModalBody`.
   */
  isBodyColumn?: boolean
  /**
   * Abas do modal. A `TabList` fica **fora** do `ModalBody`, então ela não rola junto com o
   * painel — que é a razão de os três modais com aba do painel montarem o `<Tabs>` na mão.
   *
   * Rótulo e conteúdo vêm do mesmo array, então a barra e os painéis não têm como sair de
   * sincronia (montados à mão, a lista era filtrada e os painéis eram escritos um a um).
   */
  tabs?: IFormModalTab[]
  /** Aba ativa. Necessário quando a página precisa pular para a aba que tem erro. */
  tabIndex?: number
  onTabChange?: (index: number) => void
  /**
   * Monta cada painel só na primeira visita, e o mantém montado depois. Para o modal cujo
   * painel é caro de montar — uma série com muitas edições — sem perder o estado de quem já
   * foi visitado. Os contadores de erro do rótulo não dependem disso: saem do `fieldErrors`,
   * não da árvore renderizada.
   */
  areTabsLazy?: boolean
  /**
   * Desliga a validação nativa do navegador no `<form>`. Para o modal com abas que leva os erros da
   * API até a aba certa: os painéis escondidos continuam montados, e um campo `required` vazio numa
   * aba fechada faz o Chrome recusar o envio sem mostrar nada ("An invalid form control … is not
   * focusable") — o Salvar simplesmente não responde. Com a validação do servidor, o erro aparece no
   * campo e a aba ganha o contador.
   */
  noValidate?: boolean
  children?: ReactNode
}

/**
 * Casca dos modais de formulário do painel — a peça irmã do `ConfirmDialog`.
 *
 * A página declara título, tamanho e o que fazer no submit; largura, centralização, rolagem,
 * botão de fechar e ordem dos botões do rodapé vêm daqui.
 *
 * - **`isCentered` é sempre.** Os 37 modais alinhados ao topo não eram uma decisão: eram os
 *   modais mais antigos, escritos com o alias `Modal`. Os novos já eram centrados.
 * - **`scrollBehavior="inside"` é sempre**, que já era a escolha de 57 dos 58 que passavam a prop.
 *   O cabeçalho e o rodapé ficam parados e só o corpo rola, que é o que um formulário longo pede.
 * - **O corpo é um `<form>`**, então o Enter envia. Havia 7 modais com `type="submit"` num botão
 *   sem `<form>` nenhum no arquivo: o botão funcionava no clique e o Enter não fazia nada.
 * - **A ordem dos botões é a do `FormActions`** — Cancelar e depois Confirmar. 25 rodapés tinham a
 *   ordem invertida.
 */
export default function FormModal({
  isOpen,
  onClose,
  title,
  onSubmit,
  size = 'default',
  confirmAction = 'save',
  confirmLabel,
  cancelLabel,
  isLoading = false,
  isConfirmDisabled = false,
  footer,
  initialFocusRef,
  isDismissible = true,
  contentProps,
  isBodyColumn = false,
  tabs,
  tabIndex,
  onTabChange,
  areTabsLazy = false,
  noValidate = false,
  children
}: IFormModalProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit?.(event)
  }

  const body = tabs ? (
    <Tabs
      index={tabIndex}
      onChange={onTabChange}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      isLazy={areTabsLazy}
      lazyBehavior="keepMounted"
    >
      {/* A lista quebra linha em vez de deslizar. Deslizar com a barra de rolagem escondida —
          como os três modais faziam — corta a última aba sem nenhum sinal de que ela existe;
          foi assim que a aba "Local" do evento sumiu. O corpo rola por conta própria
          (`scrollBehavior="inside"`), então a segunda linha custa altura, não conteúdo. */}
      <TabList px={6} flexShrink={0} flexWrap="wrap">
        {tabs.map((tab, index) => (
          // Aparência da aba vem do tema. O `nowrap` fica aqui porque a TabList quebra linha:
          // o rótulo não pode partir no meio.
          <Tab key={index} whiteSpace="nowrap">
            {tab.label}
          </Tab>
        ))}
      </TabList>

      <ModalBody minH={0}>
        <TabPanels minH="420px">
          {tabs.map((tab, index) => (
            <TabPanel key={index} px={0}>
              {tab.content}
            </TabPanel>
          ))}
        </TabPanels>
      </ModalBody>
    </Tabs>
  ) : (
    <ModalBody {...(isBodyColumn && { display: 'flex', flexDirection: 'column' })}>{children}</ModalBody>
  )

  const content = (
    <ModalContent {...contentProps}>
      {title && <ModalHeader>{title}</ModalHeader>}
      {isDismissible && <ModalCloseButton />}

      {body}

      <ModalFooter>
        {footer ??
          (onSubmit ? (
            <FormActions
              onCancel={onClose}
              confirmType="submit"
              confirmAction={confirmAction}
              confirmLabel={confirmLabel}
              cancelLabel={cancelLabel}
              isLoading={isLoading}
              isDisabled={isConfirmDisabled}
            />
          ) : (
            <Button variant="ghost" onClick={onClose}>
              {cancelLabel ?? 'Fechar'}
            </Button>
          ))}
      </ModalFooter>
    </ModalContent>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={SIZES[size]}
      isCentered
      scrollBehavior="inside"
      initialFocusRef={initialFocusRef}
      closeOnOverlayClick={isDismissible}
      closeOnEsc={isDismissible}
    >
      {/* Um desfoque só para o painel. Eram 20 modais com overlay próprio e 6 valores entre eles
          (`blur(4px)`, `blur(6px)`, `blur(10px) saturate(1.2)`…); 4px é o que a maioria usava. */}
      <ModalOverlay backdropFilter="blur(4px)" />
      {/* O `<form>` envolve o `ModalContent`, e não o contrário: o `ModalContent` monta o próprio
          container fixo, então o form fica sendo só um wrapper de bloco em volta dele. É a forma
          que já funcionava nos 9 modais do painel que tinham form. Sem `onSubmit` o modal é de
          leitura e não há form nenhum. */}
      {onSubmit ? (
        <form onSubmit={handleSubmit} noValidate={noValidate}>
          {content}
        </form>
      ) : (
        content
      )}
    </Modal>
  )
}
